/**
 * Beehive stand (`hush.beehive_stand.v1`) — a ground piece: a low wooden bench carrying woven straw skeps.
 *
 * Frame 130×80, support at the bottom-center (65, 80). A plank bench on three legs sits on a grass strip; `hives`
 * (integer 2..3) coiled-straw skeps stand on the plank, each with one flat dark entrance slit at its base (never
 * round holes, never two dark marks stacked over one — no faces). `flowers` (0..1) sets how many wildflowers grow
 * by the legs. No definitions, no collision or gameplay geometry. Deterministic for the recipe; the seed varies
 * hive sizes, band wobble and flower placement.
 */
import {
  checkEnvelope, checkScope, el, mix, n, partGroup, poly, rngFor,
  type HideoutPieceArt, type PieceRecipe, type Point, type ResolvedPalette,
} from '../pieces/svg';

export interface BeehiveParams {
  readonly hives: number;   // integer 2..3
  readonly flowers: number; // 0..1
}
export type BeehiveRecipe = PieceRecipe<'hush.beehive_stand.v1', BeehiveParams>;

export const BEEHIVE_FRAME = { w: 130, h: 80 } as const;

export const HUSH_BEEHIVES: BeehiveRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'hush.beehive_stand.skeps.v1', generatorId: 'hush.beehive_stand.v1',
  seed: 20067018, paletteId: 'coastal_salt', params: { hives: 3, flowers: 0.7 },
};

/** rngFor's first draw is correlated across similar channel names; a murmur finalizer decorrelates it. */
function scrambled(base: (ch: string) => number): (ch: string) => number {
  return ch => {
    let h = Math.floor(base(ch) * 4294967296) >>> 0;
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

function num(p: unknown, k: string, lo: number, hi: number, int = false): number {
  const v = (p as Record<string, unknown>)[k];
  if (typeof v !== 'number' || !(v >= lo && v <= hi) || (int && !Number.isInteger(v))) {
    throw new Error(`hush.beehive_stand.v1: ${k} must be ${int ? 'an integer ' : ''}${lo}..${hi}`);
  }
  return v;
}

const W = BEEHIVE_FRAME.w, H = BEEHIVE_FRAME.h;
const PLANK_TOP = 50, PLANK_H = 6, GROUND = H - 4;

/** Skep half-width at height fraction t (0 = base, 1 = crown): a soft bell, slightly fuller low down. */
const skepHalf = (hw: number, t: number) => hw * Math.pow(Math.max(0, 1 - Math.pow(t, 2.3)), 0.5) * (1 + 0.07 * Math.sin(Math.PI * t));

function skep(cx: number, by: number, hw: number, hh: number, roles: ResolvedPalette, wob: (k: number) => number): string {
  const K = 16;
  const left: Point[] = [], right: Point[] = [];
  for (let k = 0; k <= K; k++) {
    const t = k / K, y = by - hh * t, h = skepHalf(hw, t);
    left.push({ x: cx - h, y });
    right.unshift({ x: cx + h, y });
  }
  const outline = roles.outline;
  const straw = mix(roles.woodLight, roles.warmCore, 0.45);
  const strawShadow = mix(roles.woodBase, roles.warmCore, 0.2);
  const strawLight = mix(roles.warmCore, roles.paperLight, 0.5);
  let s = el('path', { d: poly([...left, ...right]), fill: straw, stroke: outline, 'stroke-width': 1.3, 'stroke-linejoin': 'round' });
  // Shade the right flank (light from the upper left).
  const shade: Point[] = [];
  for (let k = 0; k <= K; k++) { const t = k / K; shade.push({ x: cx + skepHalf(hw, t) * 0.45, y: by - hh * t }); }
  for (let k = K; k >= 0; k--) { const t = k / K; shade.push({ x: cx + skepHalf(hw, t) - 0.6, y: by - hh * t }); }
  s += el('path', { d: poly(shade), fill: strawShadow, opacity: 0.35 });
  // Coiled straw bands: shallow sagging arcs, each with a pale ridge above its shadow line.
  const bands = Math.max(5, Math.round(hh / 5));
  for (let b = 1; b <= bands; b++) {
    const t = b / (bands + 0.6), y = by - hh * t + wob(b) * 0.5, h = skepHalf(hw, t) - 0.4;
    if (h < 2.5) continue;
    const sag = 1.6 + 1.4 * (1 - t);
    s += el('path', { d: `M${n(cx - h)} ${n(y)}Q${n(cx)} ${n(y + sag * 2)} ${n(cx + h)} ${n(y)}`,
      fill: 'none', stroke: strawShadow, 'stroke-width': 1, 'stroke-linecap': 'round' });
    s += el('path', { d: `M${n(cx - h * 0.85)} ${n(y - 1.4)}Q${n(cx - h * 0.2)} ${n(y + sag * 1.4 - 1.4)} ${n(cx + h * 0.35)} ${n(y + sag * 1.2 - 1.4)}`,
      fill: 'none', stroke: strawLight, 'stroke-width': 0.8, 'stroke-linecap': 'round', opacity: 0.75 });
  }
  // Crown tuft so the top reads as twisted straw, not a cap.
  s += el('path', { d: `M${n(cx - 2.5)} ${n(by - hh + 1.2)}Q${n(cx - 0.5)} ${n(by - hh - 3.5)} ${n(cx + 2.2)} ${n(by - hh + 0.8)}`,
    fill: 'none', stroke: strawShadow, 'stroke-width': 1.4, 'stroke-linecap': 'round' });
  // One flat entrance slit at the base — a wide, low arch, never a round hole.
  const sw = Math.min(6, hw * 0.4);
  s += el('path', { d: `M${n(cx - sw)} ${n(by - 0.4)}Q${n(cx)} ${n(by - 3.6)} ${n(cx + sw)} ${n(by - 0.4)}Z`, fill: outline });
  return s;
}

function flower(x: number, h: number, petal: string, roles: ResolvedPalette): string {
  const top = { x: x + 1.5, y: GROUND - h };
  let f = el('path', { d: `M${n(x)} ${n(GROUND + 0.5)}Q${n(x - 1)} ${n(GROUND - h * 0.5)} ${n(top.x)} ${n(top.y)}`,
    fill: 'none', stroke: roles.reedShadow, 'stroke-width': 1.1, 'stroke-linecap': 'round' });
  f += el('path', { d: `M${n(x - 0.4)} ${n(GROUND - h * 0.35)}q-4 -1 -5 -4q3.5 0 5 3Z`, fill: roles.reedBase, stroke: roles.reedShadow, 'stroke-width': 0.5 });
  for (let i = 0; i < 5; i++) {
    const a = (Math.PI * 2 * i) / 5 - Math.PI / 2;
    f += el('ellipse', { cx: top.x + Math.cos(a) * 2.2, cy: top.y + Math.sin(a) * 2.2, rx: 1.7, ry: 1.7, fill: petal, stroke: roles.outlineSoft, 'stroke-width': 0.4 });
  }
  f += el('ellipse', { cx: top.x, cy: top.y, rx: 1.2, ry: 1.2, fill: roles.warmCore });
  return f;
}

export function buildBeehives(recipe: BeehiveRecipe, roles: ResolvedPalette, scope: string): HideoutPieceArt {
  checkEnvelope(recipe, 'hush.beehive_stand.v1');
  checkScope(scope);
  const hives = num(recipe.params, 'hives', 2, 3, true);
  const flowers = num(recipe.params, 'flowers', 0, 1);
  const rnd = scrambled(rngFor(recipe));
  const parts: string[] = [];
  const outline = roles.outline;

  // ---- ground: a low grass/earth strip the legs stand in; it carries the support contact ----
  let ground = el('path', {
    d: `M6 ${H}Q10 ${GROUND - 1} 22 ${GROUND}L108 ${GROUND - 0.5}Q120 ${GROUND - 1} 124 ${H}Z`,
    fill: roles.moss, stroke: outline, 'stroke-width': 1, 'stroke-linejoin': 'round',
  });
  for (let i = 0; i < 14; i++) {
    const x = 12 + i * 8 + (rnd(`grass.${i}`) - 0.5) * 3, h = 3 + rnd(`grass.${i}.h`) * 4;
    ground += el('path', { d: `M${n(x - 1.4)} ${n(GROUND + 1)}Q${n(x)} ${n(GROUND - h * 0.6)} ${n(x + (i % 2 ? 1.8 : -1.8))} ${n(GROUND - h)}Q${n(x + 0.5)} ${n(GROUND - h * 0.4)} ${n(x + 1.4)} ${n(GROUND + 1)}Z`,
      fill: i % 3 ? roles.reedBase : roles.reedShadow });
  }

  // ---- bench: plank on three legs ----
  let bench = '';
  for (const lx of [16, W / 2, W - 16]) {
    bench += el('path', { d: poly([{ x: lx - 3, y: PLANK_TOP + PLANK_H - 1 }, { x: lx + 3, y: PLANK_TOP + PLANK_H - 1 }, { x: lx + 3.4, y: GROUND + 1.5 }, { x: lx - 3.4, y: GROUND + 1.5 }]),
      fill: roles.woodShadow, stroke: outline, 'stroke-width': 1.1, 'stroke-linejoin': 'round' });
  }
  bench += el('path', { d: `M10 ${GROUND - 9}L${W - 10} ${GROUND - 9.5}`, stroke: roles.woodShadow, 'stroke-width': 2.2, 'stroke-linecap': 'round' });
  bench += el('path', { d: poly([{ x: 6, y: PLANK_TOP }, { x: W - 6, y: PLANK_TOP - 0.6 }, { x: W - 7, y: PLANK_TOP + PLANK_H }, { x: 7, y: PLANK_TOP + PLANK_H }]),
    fill: roles.woodBase, stroke: outline, 'stroke-width': 1.3, 'stroke-linejoin': 'round' });
  bench += el('path', { d: `M9 ${PLANK_TOP + 1.4}L${W - 9} ${PLANK_TOP + 0.8}`, stroke: roles.woodLight, 'stroke-width': 1, opacity: 0.8 });
  for (let i = 0; i < 3; i++) {
    const x = 20 + rnd(`grain.${i}`) * 85;
    bench += el('path', { d: `M${n(x)} ${n(PLANK_TOP + 3.4)}l${n(8 + rnd(`grain.${i}.l`) * 8)} 0.3`, stroke: roles.woodShadow, 'stroke-width': 0.6, opacity: 0.6 });
  }

  // ---- skeps on the plank ----
  const slots = hives === 2 ? [40, 90] : [27, 65, 103];
  let skeps = '';
  slots.forEach((sx, i) => {
    const hw = (hives === 2 ? 17 : 14.5) + (rnd(`hive.${i}.w`) - 0.5) * 3;
    const hh = 34 + rnd(`hive.${i}.h`) * 9;
    const cx = sx + (rnd(`hive.${i}.x`) - 0.5) * 4;
    skeps += skep(cx, PLANK_TOP + 0.4, hw, hh, roles, b => rnd(`hive.${i}.b${b}`) - 0.5);
  });

  // ---- wildflowers by the legs ----
  const nf = Math.round(flowers * 3);
  const spots = [8, 38, 92, 122];
  const start = Math.floor(rnd('flower.start') * spots.length);
  let blooms = '';
  for (let i = 0; i < nf; i++) {
    const x = spots[(start + i * 2 + (i > 1 ? 1 : 0)) % spots.length] + (rnd(`flower.${i}.x`) - 0.5) * 4;
    const petal = rnd(`flower.${i}.c`) < 0.5 ? roles.salt : roles.clothBlueLight;
    blooms += flower(Math.min(W - 6, Math.max(6, x)), 10 + rnd(`flower.${i}.h`) * 8, petal, roles);
  }

  const body = partGroup(parts, 'beehive-ground', ground)
    + partGroup(parts, 'beehive-bench', bench)
    + partGroup(parts, 'beehive-skeps', skeps)
    + (blooms ? partGroup(parts, 'beehive-flowers', blooms) : '');
  const support = { x: W / 2, y: H };
  return {
    frame: { ...BEEHIVE_FRAME },
    body,
    anchors: { support, bench_top: { x: W / 2, y: PLANK_TOP } },
    parts,
  };
}

export default HUSH_BEEHIVES;
